import {
  OnInit,
  Injector,
  OnDestroy,
  HostListener,
  Directive,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { HttpErrorResponse } from "@angular/common/http";
import { Subscription, of as observableOf, Observable } from "rxjs";
import { switchMap } from "rxjs/operators";

import { BaseModel } from "src/app/shared/models/base.model";
import { CanDeactivateForm, CanDeactivateGuard } from "src/app/shared/guards";

import {
  getFieldError,
  getFieldErrorMsg,
  markAsTouched,
  setFieldError,
  setFocusError,
} from "src/app/shared/utils/form";
import { showErrorInToastr } from "src/app/shared/utils";
import {
  RegisterAction,
  RegisterHeaderConfig,
  RegisterHeaderEvent,
} from "src/app/shared/components/register-header/register-header.component";
import { StatusComponent } from "src/app/shared/components/status/status.component";

// Services
import { BaseApiService } from "src/app/shared/services/base-api.service";
import { ResourceIdListService } from "src/app/shared/services/resource-id-list.service";
import { RouterService } from "src/app/shared/services/router.service";
import { ToastrMsgService } from "src/app/shared/services/toastr-msg.service";
import { PreviousRouteService } from "src/app/shared/services";
import { Status } from "src/app/shared/interfaces";

@Directive()
export abstract class BaseFormComponent<T extends BaseModel>
  implements OnInit, OnDestroy, CanDeactivateForm {

  @ViewChild('container') containerRef!: ElementRef<HTMLElement>;

  private previousRoute: PreviousRouteService;

  protected mainHeight = document.getElementsByClassName('body')[0].clientHeight - 96;

  @ViewChild("status") status: StatusComponent;
  public statusInfo: Status = null;

  currentAction: string;
  resourceForm: UntypedFormGroup;
  title: string;
  serverErrorMessages: string[] = null;
  submittingForm = false;

  config: RegisterHeaderConfig = { actions: [] };

  public goToPrevPage: boolean;
  public resourceIdList: number[] = [];

  protected queryParam: ParamMap = null;

  protected route: ActivatedRoute;
  protected router: Router;
  protected routerService: RouterService;
  protected toastrMsg: ToastrMsgService;
  protected formBuilder: UntypedFormBuilder;
  protected resourceListService: ResourceIdListService;
  protected previousUrl: string;
  protected subscriptions: Subscription[] = [];

  constructor(
    protected injector: Injector,
    protected resource: T,
    protected baseApiService: BaseApiService<T>,
    protected jsonDataToResourceFn: (jsonData) => T,
    protected namespace?: string,
    protected prefixAction?: string
  ) {
    if (!this.namespace) {
      this.namespace = this.injector.get<string>(<any>"NS", null);
    }

    if (!this.prefixAction) {
      this.prefixAction = this.injector.get<string>(<any>"AP", null);
    }

    this.route = this.injector.get(ActivatedRoute);
    this.router = this.injector.get(Router);
    this.routerService = this.injector.get(RouterService);
    this.toastrMsg = this.injector.get(ToastrMsgService);
    this.formBuilder = this.injector.get(UntypedFormBuilder);
    this.resourceListService = this.injector.get(ResourceIdListService);
    this.previousRoute = this.injector.get(PreviousRouteService);

    this.route.routeConfig.canDeactivate = [CanDeactivateGuard];
  }

  ngOnInit() {
    this.setCurrentAction();
    // this.buildResourceForm(null);
    this.loadResource();
    this.setPageTitle();

    this.subscriptions.push(
      this.previousRoute.previousUrl.subscribe((res) => {
        this.previousUrl = res;
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());

    this.resourceListService.setResourceIdList(null);
    sessionStorage.removeItem("base-list-ids");
  }

  // ngAfterViewInit() {
  //   this.setPageTitle();
  // }

  @HostListener("window:beforeunload")
  canDeactivate() {
    // if (this.submittingForm || !this.resourceIsChanged(this.resourceForm, this.resource) ) {
    //   sessionStorage.setItem('base-list-ids', this.resourceIdList?.toString());

    //   return true;
    // }

    return true;
  }

  // PRIVATE METHODS

  protected setCurrentAction() {
    const urls = this.route.snapshot.url;
    if (urls.length > 0) {
      this.currentAction = urls[0].path === "new" ? "new" : "edit";
    } else {
      this.currentAction = null;
    }
  }

  protected setResource(resource: T): void {
    this.resource = resource;
    this.hideStatus();
  }

  protected switchMapParams(params): Observable<T | null> {
    const itemId = params.get("id");
    if (!itemId || itemId === 0) {
      return observableOf(null);
    }

    return this.baseApiService.findById(itemId);
  }

  protected loadResource() {
    if (this.currentAction === "edit" && this.baseApiService) {
      this.setStatus({ info: "Carregando Registro...", load: true });

      this.subscriptions.push(
        this.route.queryParamMap.subscribe((queryParams) => {
          this.queryParam = queryParams;
          this.subscriptions.push(
            this.route.paramMap
              .pipe(switchMap((params) => this.switchMapParams(params)))
              .subscribe(
                (resource) => {
                  const resourceData = !this.jsonDataToResourceFn
                    ? resource
                    : this.jsonDataToResourceFn(resource);
                  this.setResource(resourceData);
                  this.buildResourceForm(this.resource);
                  this.setPageTitle();
                },
                (error) => {
                  this.hideStatus();
                  this.toastrMsg.error(
                    "Ocorreu um erro no servidor, tente mais tarde."
                  );
                }
              )
          );
        })
      );
    } else {
      this.buildResourceForm(null);
    }

    this.routerService
      .InsertOrUpdateDef({
        route: this.route,
        namespace: this.namespace,
        prefix: this.prefixAction,
      })
      .then((perm) => {
        this.config.hidenBtn = { delete: true };
        this.config.permissions = {
          namespace: perm.namespace,
          save: perm.action,
          delete: `${this.prefixAction}_delete`,
        };
      });

    this.subscriptions.push(
      this.resourceListService.getResourceIdList().subscribe((res) => {
        if (res) {
          this.resourceIdList = res;
        } else {
          const storage = sessionStorage.getItem("base-list-ids");
          if (storage) {
            this.resourceIdList = storage.split(",").map((item) => +item);
          }
        }
      })
    );
  }

  protected setPageTitle() {
    const fn =
      this.currentAction === "new"
        ? this.creationPageTitle.bind(this)
        : this.editionPageTitle.bind(this);
    this.title = fn();
  }

  protected creationPageTitle(): string {
    return "Novo";
  }

  protected editionPageTitle(): string {
    return "Edição";
  }

  protected customNavigatePath(resource: T): string[] {
    return [];
  }

  protected doNavigate(paths: string[]): Promise<boolean> {
    return this.router
      .navigate(paths, {
        relativeTo: this.route,
        queryParamsHandling: "merge",
      });
  }

  protected actionsForSuccess(resource: T): void {
    this.toastrMsg.success("Solicitação processada com sucesso!");

    const paths = [".."];
    if (!this.goToPrevPage) {
      paths.push(String(resource.id));
      this.doNavigate(paths).then(() => {
        this.buildResourceForm(resource);
        this.submittingForm = false;
      });
    } else {
      const customNavPath = this.customNavigatePath(resource);
      if (customNavPath && customNavPath.length > 0) {
        paths.push(...customNavPath);

        this.doNavigate(paths).then(() => {
          this.submittingForm = false;
        });
      } else if (this.previousUrl) {
        this.router
          .navigateByUrl(this.previousUrl, /* Removed unsupported properties by Angular migration: relativeTo, queryParamsHandling. */ {})
          .then(() => {
            return;
          });
      } else {
        this.doNavigate([]).then(() => {
          this.submittingForm = false;
        });
      }

    }
  }

  protected actionsForError(respError: HttpErrorResponse, mapFields?: Object) {
    if (respError.status !== 500) {
      showErrorInToastr(this.toastrMsg, respError, mapFields);
    } else {
      showErrorInToastr(
        this.toastrMsg,
        "Erro ao processar solicitação! Tente novamente mais tarde..."
      );
    }
    console.log("Ocorreu um erro ao processar a sua solicitação!", respError);

    if (respError.status === 400) {
      setFieldError(this.resourceForm, respError.error);
    } else if (respError.status === 422) {
      this.serverErrorMessages = JSON.parse(respError.error);
    } else {
      this.serverErrorMessages = [
        "Falha na comunicação com o servidor. Por favor, tente mais tarde.",
      ];
    }

    this.submittingForm = false;
    markAsTouched(this.resourceForm);
    setFocusError(this.resourceForm);
  }

  protected abstract buildResourceForm(resource: T): void;

  // Poderá ser implementado para manipulação do resource antes de salvar
  protected beforeSaveResource(resource: T): T {
    return resource;
  }

  protected validateResource(resource: T): boolean {
    return true;
  }

  protected doSaveData(resource: T): Observable<T> {
    if (this.validateResource(resource)) {
      return this.baseApiService.save(resource);
    }
  }

  protected setStatus(status: Status): void {
    this.statusInfo = status;

    if (status && this.status) {
      this.status.setStatus(status);
      this.status.visible = true;
    }
  }

  protected hideStatus(): void {
    this.statusInfo = null;

    if (this.status) {
      this.status.hide();
    }
  }

  protected doValidateForm(): boolean {
    if (!this.resourceForm) {
      return false;
    }

    if (!this.resourceForm.valid) {
      console.log("Formulário inválido", this.resourceForm);
      markAsTouched(this.resourceForm);
      setFocusError(this.resourceForm);

      const fieldsError = getFieldError(this.resourceForm);
      const resource: T = this.beforeSaveResource(
        this.jsonDataToResourceFn(this.resourceForm.getRawValue())
      );
      const mapFields = resource.getMapFields();
      const fieldErrorMsg = getFieldErrorMsg(fieldsError, mapFields);
      this.toastrMsg.error(`<b>Erros:<b>${fieldErrorMsg}`, "CAMPOS INVÁLIDOS");
      return false;
    }

    return true;
  }

  protected doSaveResource(callbackFn: any, ret: boolean = false): void {
    if (!this.submittingForm) {
      this.submittingForm = true;
      this.goToPrevPage = ret;

      try {
        const resource: T = this.beforeSaveResource(
          this.jsonDataToResourceFn(this.resourceForm.getRawValue())
        );
        this.subscriptions.push(
          this.doSaveData(resource).subscribe(
            (returnedData) => {
              if (!!callbackFn) {
                callbackFn();
              }
              this.actionsForSuccess(this.jsonDataToResourceFn(returnedData));
            },
            (error) => {
              if (!!callbackFn) {
                callbackFn();
              }
              this.actionsForError(error, resource.getMapFields());
            }
          )
        );
      } catch (error) {
        this.submittingForm = false;
        if (!!callbackFn) {
          callbackFn();
        }
      }
    }
  }

  public saveResource(event: RegisterHeaderEvent, ret: boolean = false) {
    if (!this.doValidateForm()) {
      return;
    }

    if (event) {
      event.promise((done) => {
        this.doSaveResource(done, ret);
      })
    } else {
      this.doSaveResource(null, ret);
    }
  }

  public addConfigActions(actions: RegisterAction[]): void {
    if (!this.config.actions) {
      this.config.actions = [];
    }

    this.config.actions.push(...actions);

    // Forced render template
    this.config = { ...this.config };
  }

  public onReturn(event: Event): void {
    if (this.previousUrl) {
      this.router.navigateByUrl(this.previousUrl, /* Removed unsupported properties by Angular migration: relativeTo, queryParamsHandling. */ {});
    } else {
      this.router.navigate([".."], {
        relativeTo: this.route,
        queryParamsHandling: "merge",
      });
    }
  }
}
